/**
 * Leverage Utility Functions
 * Helper functions for leverage operations and validation
 */

import { getMarketByTokenIndex, getMarketByMarketIndex } from '../config/markets';

/**
 * Get maximum leverage for a market by token index
 * @param {number|string} tokenIndex - TokenIndex number or string
 * @returns {number} Maximum leverage (default: 100)
 */
export const getMaxLeverage = (tokenIndex) => {
  const market = getMarketByTokenIndex(tokenIndex);
  return market?.maxLeverage || 100;
};

/**
 * Get maximum leverage for a market by market index
 * @param {number|string} marketIndex - MarketIndex number or string
 * @returns {number} Maximum leverage (default: 100)
 */
export const getMaxLeverageByMarketIndex = (marketIndex) => {
  const market = getMarketByMarketIndex(marketIndex);
  return market?.maxLeverage || 100;
};

/**
 * Resolve the maximum leverage the UI may offer for a market.
 *
 * The contract is the authority: whatever the keeper reports from the on-chain
 * market config wins, even when it is lower than the static entry in
 * `config/markets.js`. A market configured for 100x locally but capped at 50x
 * on-chain must offer 50x, otherwise the ticket lets a trader pick a leverage
 * the contract rejects at open time. The static config is only a fallback for
 * the window before the keeper responds (or when it is unreachable).
 *
 * @param {number|string|null|undefined} contractMaxLeverage - maxLeverage as
 *   reported by the keeper's `perp.borrowing` query (on-chain market config)
 * @param {number} [configMaxLeverage=100] - static fallback from markets.js
 * @returns {number} Maximum leverage to offer
 */
export const resolveMaxLeverage = (contractMaxLeverage, configMaxLeverage = 100) => {
  const fromContract = Math.floor(Number(contractMaxLeverage));
  if (Number.isFinite(fromContract) && fromContract >= 1) {
    return fromContract;
  }
  const fromConfig = Math.floor(Number(configMaxLeverage));
  return Number.isFinite(fromConfig) && fromConfig >= 1 ? fromConfig : 100;
};

/**
 * Validate leverage value
 * @param {number} leverage - Leverage value to validate
 * @param {number} maxLeverage - Maximum allowed leverage
 * @returns {Object} Validation result with isValid and error message
 */
export const validateLeverage = (leverage, maxLeverage = 100) => {
  if (leverage < 1) {
    return {
      isValid: false,
      error: 'Leverage must be at least 1x'
    };
  }
  
  if (leverage > maxLeverage) {
    return {
      isValid: false,
      error: `Leverage cannot exceed ${maxLeverage}x`
    };
  }
  
  if (isNaN(leverage) || !isFinite(leverage)) {
    return {
      isValid: false,
      error: 'Leverage must be a valid number'
    };
  }
  
  return {
    isValid: true,
    error: null
  };
};

/**
 * Clamp leverage value to valid range
 * @param {number} leverage - Leverage value to clamp
 * @param {number} maxLeverage - Maximum allowed leverage
 * @returns {number} Clamped leverage value
 */
export const clampLeverage = (leverage, maxLeverage = 100) => {
  const numLeverage = Number(leverage);
  if (isNaN(numLeverage) || !isFinite(numLeverage)) {
    return 1;
  }
  return Math.max(1, Math.min(maxLeverage, numLeverage));
};

/**
 * Generate leverage marks for slider component
 * @param {number} maxLeverage - Maximum leverage
 * @returns {Object} Marks object for rc-slider
 */
export const generateLeverageMarks = (maxLeverage) => {
  const marks = { 1: "1x" };
  
  if (maxLeverage >= 100) {
    marks[25] = "25x";
    marks[50] = "50x";
    marks[75] = "75x";
    marks[100] = "100x";
  } else if (maxLeverage >= 50) {
    const quarter = Math.floor(maxLeverage / 4);
    const half = Math.floor(maxLeverage / 2);
    const threeQuarter = Math.floor(maxLeverage * 3 / 4);
    
    marks[quarter] = `${quarter}x`;
    marks[half] = `${half}x`;
    marks[threeQuarter] = `${threeQuarter}x`;
    marks[maxLeverage] = `${maxLeverage}x`;
  } else if (maxLeverage >= 20) {
    const quarter = Math.floor(maxLeverage / 4);
    const half = Math.floor(maxLeverage / 2);
    
    marks[quarter] = `${quarter}x`;
    marks[half] = `${half}x`;
    marks[maxLeverage] = `${maxLeverage}x`;
  } else {
    const half = Math.floor(maxLeverage / 2);
    marks[half] = `${half}x`;
    marks[maxLeverage] = `${maxLeverage}x`;
  }
  
  return marks;
};

/**
 * Get leverage options array for dropdown/select components
 * @param {number} maxLeverage - Maximum leverage
 * @param {number} step - Step size (default: 1)
 * @returns {Array} Array of leverage options
 */
export const getLeverageOptions = (maxLeverage = 100, step = 1) => {
  const options = [];
  for (let i = 1; i <= maxLeverage; i += step) {
    options.push({
      value: i,
      label: `${i}x`
    });
  }
  return options;
};

/**
 * Format leverage value for display
 * @param {number} leverage - Leverage value
 * @returns {string} Formatted leverage string (e.g., "10x")
 */
export const formatLeverage = (leverage) => {
  const numLeverage = Number(leverage);
  if (isNaN(numLeverage) || !isFinite(numLeverage)) {
    return "1x";
  }
  return `${numLeverage}x`;
};

